/**
 * Analytics Service for Backup Task Analysis
 *
 * Provides pure functions for analyzing backup tasks, calculating trends,
 * forecasting growth, and detecting anomalies. All functions are side-effect free.
 *
 * @module analyticsService
 */

/**
 * Task data structure:
 * @typedef {Object} Task
 * @property {string} id - Unique task identifier
 * @property {string} created_at - ISO timestamp when task was created
 * @property {string} finished_at - ISO timestamp when task finished
 * @property {string} status - Task status: 'success', 'failed', 'running', 'pending'
 * @property {string} resource_id - ID of the resource being backed up
 * @property {number} [duration] - Task duration in milliseconds
 * @property {number} [size] - Data size in bytes
 */

/**
 * Resource data structure:
 * @typedef {Object} Resource
 * @property {string} id - Unique resource identifier
 * @property {string} name - Resource name
 * @property {string} created_at - ISO timestamp when resource was first backed up
 */

/**
 * Archive data structure:
 * @typedef {Object} Archive
 * @property {string} created_at - ISO timestamp of archive
 * @property {number} size - Archive size in bytes
 * @property {string} resource_id - Related resource ID
 */

/**
 * Period types for grouping data
 * @typedef {'day' | 'week' | 'month'} Period
 */

/**
 * Calculate historical trends by grouping tasks into time periods
 * and analyzing success rates, average durations, and identifying trends.
 *
 * @param {Task[]} tasks - Array of backup tasks
 * @param {Period} period - Time period for grouping ('day', 'week', 'month')
 * @returns {Object} Historical trends data
 * @returns {Array} returns.periods - Array of period data with metrics
 * @returns {Object} returns.overall - Overall statistics across all periods
 * @returns {Object} returns.trends - Identified trends (improving, declining, stable)
 *
 * @example
 * const trends = calculateHistoricalTrends(tasks, 'week');
 * console.log(trends.periods); // Weekly breakdown
 * console.log(trends.overall.successRate); // 95.5
 * console.log(trends.trends.direction); // 'improving'
 */
export function calculateHistoricalTrends(tasks, period = 'day') {
  if (!tasks || tasks.length === 0) {
    return {
      periods: [],
      overall: { successRate: 0, avgDuration: 0, totalTasks: 0 },
      trends: { direction: 'stable', change: 0 }
    };
  }

  // Filter out tasks that haven't finished
  const completedTasks = tasks.filter(t => t.finished_at && t.status);

  if (completedTasks.length === 0) {
    return {
      periods: [],
      overall: { successRate: 0, avgDuration: 0, totalTasks: 0 },
      trends: { direction: 'stable', change: 0 }
    };
  }

  // Group tasks by period
  const grouped = {};
  const periodMs = {
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000
  }[period];

  completedTasks.forEach(task => {
    const timestamp = new Date(task.finished_at).getTime();
    const periodKey = Math.floor(timestamp / periodMs) * periodMs;

    if (!grouped[periodKey]) {
      grouped[periodKey] = [];
    }
    grouped[periodKey].push(task);
  });

  // Calculate metrics for each period
  const periods = Object.entries(grouped)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([timestamp, periodTasks]) => {
      const successCount = periodTasks.filter(t => t.status === 'success').length;
      const failedCount = periodTasks.filter(t => t.status === 'failed').length;
      const total = periodTasks.length;
      const successRate = (successCount / total) * 100;

      // Calculate average duration (in milliseconds)
      const durations = periodTasks
        .filter(t => t.finished_at && t.created_at)
        .map(t => new Date(t.finished_at).getTime() - new Date(t.created_at).getTime());
      const avgDuration = durations.length > 0
        ? durations.reduce((sum, d) => sum + d, 0) / durations.length
        : 0;

      // Calculate total data size
      const totalSize = periodTasks
        .filter(t => t.size)
        .reduce((sum, t) => sum + t.size, 0);

      return {
        timestamp: Number(timestamp),
        date: new Date(Number(timestamp)).toISOString(),
        period,
        total,
        successCount,
        failedCount,
        successRate: Math.round(successRate * 100) / 100,
        avgDuration: Math.round(avgDuration),
        totalSize,
        avgSize: totalSize / periodTasks.length
      };
    });

  // Calculate overall statistics
  const totalTasks = completedTasks.length;
  const totalSuccess = completedTasks.filter(t => t.status === 'success').length;
  const overallSuccessRate = (totalSuccess / totalTasks) * 100;

  const allDurations = completedTasks
    .filter(t => t.finished_at && t.created_at)
    .map(t => new Date(t.finished_at).getTime() - new Date(t.created_at).getTime());
  const overallAvgDuration = allDurations.length > 0
    ? allDurations.reduce((sum, d) => sum + d, 0) / allDurations.length
    : 0;

  // Identify trends by comparing recent periods to older ones
  const trends = identifyTrend(periods);

  return {
    periods,
    overall: {
      successRate: Math.round(overallSuccessRate * 100) / 100,
      avgDuration: Math.round(overallAvgDuration),
      totalTasks,
      successCount: totalSuccess,
      failedCount: totalTasks - totalSuccess
    },
    trends
  };
}

/**
 * Helper function to identify trends from period data
 * @private
 */
function identifyTrend(periods) {
  if (periods.length < 2) {
    return { direction: 'stable', change: 0, confidence: 'low' };
  }

  // Compare last 3 periods to previous 3 periods
  const recentCount = Math.min(3, Math.floor(periods.length / 2));
  const recent = periods.slice(-recentCount);
  const previous = periods.slice(-recentCount * 2, -recentCount);

  if (previous.length === 0) {
    return { direction: 'stable', change: 0, confidence: 'low' };
  }

  const recentAvgSuccess = recent.reduce((sum, p) => sum + p.successRate, 0) / recent.length;
  const previousAvgSuccess = previous.reduce((sum, p) => sum + p.successRate, 0) / previous.length;

  const change = recentAvgSuccess - previousAvgSuccess;
  const changePercent = (change / previousAvgSuccess) * 100;

  let direction = 'stable';
  if (Math.abs(changePercent) > 5) {
    direction = changePercent > 0 ? 'improving' : 'declining';
  }

  return {
    direction,
    change: Math.round(change * 100) / 100,
    changePercent: Math.round(changePercent * 100) / 100,
    confidence: periods.length >= 6 ? 'high' : periods.length >= 3 ? 'medium' : 'low',
    recentAvgSuccess: Math.round(recentAvgSuccess * 100) / 100,
    previousAvgSuccess: Math.round(previousAvgSuccess * 100) / 100
  };
}

/**
 * Calculate protection timeline for resources, showing when backups started,
 * days protected, backup frequency, and at-risk detection.
 *
 * @param {Resource[]} resources - Array of backup resources
 * @param {Task[]} tasks - Array of backup tasks
 * @returns {Array} Array of resource protection timelines
 *
 * @example
 * const timeline = calculateProtectionTimeline(resources, tasks);
 * timeline.forEach(item => {
 *   console.log(`${item.resourceName}: ${item.daysProtected} days, ${item.status}`);
 * });
 */
export function calculateProtectionTimeline(resources, tasks) {
  if (!resources || resources.length === 0 || !tasks || tasks.length === 0) {
    return [];
  }

  const now = Date.now();
  const oneDayMs = 24 * 60 * 60 * 1000;

  return resources.map(resource => {
    // Get all tasks for this resource
    const resourceTasks = tasks
      .filter(t => t.resource_id === resource.id && t.finished_at)
      .sort((a, b) => new Date(a.finished_at).getTime() - new Date(b.finished_at).getTime());

    if (resourceTasks.length === 0) {
      return {
        resourceId: resource.id,
        resourceName: resource.name,
        firstBackupDate: null,
        lastBackupDate: null,
        daysProtected: 0,
        totalBackups: 0,
        successfulBackups: 0,
        failedBackups: 0,
        frequency: 0,
        avgFrequency: 0,
        status: 'at-risk',
        reason: 'No backups found',
        daysSinceLastBackup: null
      };
    }

    const firstBackup = resourceTasks[0];
    const lastBackup = resourceTasks[resourceTasks.length - 1];
    const successfulTasks = resourceTasks.filter(t => t.status === 'success');

    const firstBackupDate = new Date(firstBackup.finished_at);
    const lastBackupDate = new Date(lastBackup.finished_at);
    const daysProtected = Math.floor((now - firstBackupDate.getTime()) / oneDayMs);
    const daysSinceLastBackup = Math.floor((now - lastBackupDate.getTime()) / oneDayMs);

    // Calculate backup frequency (backups per day)
    const timeSpanDays = Math.max(1, (lastBackupDate.getTime() - firstBackupDate.getTime()) / oneDayMs);
    const frequency = resourceTasks.length / timeSpanDays;

    // Calculate average time between backups
    const intervals = [];
    for (let i = 1; i < resourceTasks.length; i++) {
      const interval = new Date(resourceTasks[i].finished_at).getTime() -
                      new Date(resourceTasks[i - 1].finished_at).getTime();
      intervals.push(interval);
    }
    const avgInterval = intervals.length > 0
      ? intervals.reduce((sum, i) => sum + i, 0) / intervals.length
      : 0;
    const avgFrequencyDays = avgInterval > 0 ? avgInterval / oneDayMs : 0;

    // Determine status and risk
    let status = 'healthy';
    let reason = 'Regular backups detected';

    // At-risk if no backup in 7+ days
    if (daysSinceLastBackup > 7) {
      status = 'at-risk';
      reason = `No backup for ${daysSinceLastBackup} days`;
    }
    // Warning if no backup in 3-7 days
    else if (daysSinceLastBackup > 3) {
      status = 'warning';
      reason = `No backup for ${daysSinceLastBackup} days`;
    }
    // At-risk if high failure rate (>30%)
    else if (resourceTasks.length > 3) {
      const recentTasks = resourceTasks.slice(-5);
      const recentFailures = recentTasks.filter(t => t.status === 'failed').length;
      const failureRate = (recentFailures / recentTasks.length) * 100;

      if (failureRate > 30) {
        status = 'at-risk';
        reason = `High failure rate: ${Math.round(failureRate)}%`;
      } else if (failureRate > 15) {
        status = 'warning';
        reason = `Elevated failure rate: ${Math.round(failureRate)}%`;
      }
    }

    return {
      resourceId: resource.id,
      resourceName: resource.name,
      firstBackupDate: firstBackupDate.toISOString(),
      lastBackupDate: lastBackupDate.toISOString(),
      daysProtected,
      daysSinceLastBackup,
      totalBackups: resourceTasks.length,
      successfulBackups: successfulTasks.length,
      failedBackups: resourceTasks.length - successfulTasks.length,
      frequency: Math.round(frequency * 100) / 100,
      avgFrequencyDays: Math.round(avgFrequencyDays * 100) / 100,
      status,
      reason
    };
  }).sort((a, b) => {
    // Sort by status priority: at-risk first, then warning, then healthy
    const statusPriority = { 'at-risk': 0, 'warning': 1, 'healthy': 2 };
    return statusPriority[a.status] - statusPriority[b.status];
  });
}

/**
 * Forecast future data growth using linear regression on historical archive sizes.
 * Projects growth for 30, 60, and 90 days.
 *
 * @param {Archive[]} archives - Array of archive records with size and timestamp
 * @returns {Object} Growth forecast data
 * @returns {Object} returns.regression - Regression parameters (slope, intercept)
 * @returns {Object} returns.current - Current size and date
 * @returns {Object} returns.forecast - Forecasted sizes for 30/60/90 days
 * @returns {number} returns.dailyGrowthRate - Average daily growth in bytes
 *
 * @example
 * const forecast = forecastGrowth(archives);
 * console.log(`30-day forecast: ${forecast.forecast.day30.size} bytes`);
 * console.log(`Daily growth: ${forecast.dailyGrowthRate} bytes/day`);
 */
export function forecastGrowth(archives) {
  if (!archives || archives.length < 2) {
    return {
      regression: { slope: 0, intercept: 0, r2: 0 },
      current: { size: 0, date: new Date().toISOString() },
      forecast: {
        day30: { size: 0, date: null },
        day60: { size: 0, date: null },
        day90: { size: 0, date: null }
      },
      dailyGrowthRate: 0,
      confidence: 'insufficient-data'
    };
  }

  // Sort archives by date
  const sortedArchives = [...archives]
    .filter(a => a.created_at && a.size >= 0)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  if (sortedArchives.length < 2) {
    return {
      regression: { slope: 0, intercept: 0, r2: 0 },
      current: { size: 0, date: new Date().toISOString() },
      forecast: {
        day30: { size: 0, date: null },
        day60: { size: 0, date: null },
        day90: { size: 0, date: null }
      },
      dailyGrowthRate: 0,
      confidence: 'insufficient-data'
    };
  }

  // Use first archive as time zero
  const firstDate = new Date(sortedArchives[0].created_at).getTime();

  // Prepare data points (days since first archive, size in bytes)
  const dataPoints = sortedArchives.map(archive => ({
    x: (new Date(archive.created_at).getTime() - firstDate) / (24 * 60 * 60 * 1000), // days
    y: archive.size
  }));

  // Calculate linear regression: y = mx + b
  const regression = calculateLinearRegression(dataPoints);

  // Get current data
  const lastArchive = sortedArchives[sortedArchives.length - 1];
  const currentSize = lastArchive.size;
  const currentDate = new Date(lastArchive.created_at);

  // Calculate forecasts
  const now = Date.now();
  const currentDaysSinceFirst = (now - firstDate) / (24 * 60 * 60 * 1000);

  const forecasts = [30, 60, 90].map(days => {
    const futureDays = currentDaysSinceFirst + days;
    const forecastSize = Math.max(0, regression.slope * futureDays + regression.intercept);
    const forecastDate = new Date(now + days * 24 * 60 * 60 * 1000);

    return {
      days,
      size: Math.round(forecastSize),
      date: forecastDate.toISOString(),
      growthFromCurrent: Math.round(forecastSize - currentSize),
      growthPercent: currentSize > 0
        ? Math.round(((forecastSize - currentSize) / currentSize) * 10000) / 100
        : 0
    };
  });

  // Determine confidence based on R² and data quantity
  let confidence = 'low';
  if (sortedArchives.length >= 30 && regression.r2 > 0.8) {
    confidence = 'high';
  } else if (sortedArchives.length >= 10 && regression.r2 > 0.6) {
    confidence = 'medium';
  }

  return {
    regression: {
      slope: Math.round(regression.slope * 100) / 100,
      intercept: Math.round(regression.intercept),
      r2: Math.round(regression.r2 * 1000) / 1000
    },
    current: {
      size: currentSize,
      date: currentDate.toISOString()
    },
    forecast: {
      day30: forecasts[0],
      day60: forecasts[1],
      day90: forecasts[2]
    },
    dailyGrowthRate: Math.round(regression.slope * 100) / 100,
    confidence,
    dataPoints: sortedArchives.length
  };
}

/**
 * Helper function to calculate linear regression
 * @private
 */
function calculateLinearRegression(dataPoints) {
  const n = dataPoints.length;

  if (n === 0) {
    return { slope: 0, intercept: 0, r2: 0 };
  }

  const sumX = dataPoints.reduce((sum, p) => sum + p.x, 0);
  const sumY = dataPoints.reduce((sum, p) => sum + p.y, 0);
  const sumXY = dataPoints.reduce((sum, p) => sum + p.x * p.y, 0);
  const sumX2 = dataPoints.reduce((sum, p) => sum + p.x * p.x, 0);
  const sumY2 = dataPoints.reduce((sum, p) => sum + p.y * p.y, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R² (coefficient of determination)
  const meanY = sumY / n;
  const ssTotal = dataPoints.reduce((sum, p) => sum + Math.pow(p.y - meanY, 2), 0);
  const ssResidual = dataPoints.reduce((sum, p) => {
    const predicted = slope * p.x + intercept;
    return sum + Math.pow(p.y - predicted, 2);
  }, 0);
  const r2 = ssTotal !== 0 ? 1 - (ssResidual / ssTotal) : 0;

  return { slope, intercept, r2 };
}

/**
 * Generate SLA (Service Level Agreement) report showing achievement rates
 * per period and identifying breaches.
 *
 * @param {Task[]} tasks - Array of backup tasks
 * @param {number} threshold - SLA success rate threshold (default: 95%)
 * @param {Period} period - Time period for grouping ('day', 'week', 'month')
 * @returns {Object} SLA report
 * @returns {Array} returns.periods - Period-by-period SLA achievement
 * @returns {Object} returns.overall - Overall SLA metrics
 * @returns {Array} returns.breaches - Periods where SLA was breached
 *
 * @example
 * const slaReport = generateSLAReport(tasks, 95, 'week');
 * console.log(`SLA met: ${slaReport.overall.slaMet ? 'Yes' : 'No'}`);
 * console.log(`Breaches: ${slaReport.breaches.length}`);
 */
export function generateSLAReport(tasks, threshold = 95, period = 'week') {
  if (!tasks || tasks.length === 0) {
    return {
      periods: [],
      overall: {
        slaMet: false,
        successRate: 0,
        threshold,
        totalTasks: 0,
        breachCount: 0
      },
      breaches: []
    };
  }

  // Get historical trends which already groups by period
  const trends = calculateHistoricalTrends(tasks, period);

  // Analyze each period for SLA compliance
  const periods = trends.periods.map(p => {
    const slaMet = p.successRate >= threshold;
    const gap = p.successRate - threshold;

    return {
      ...p,
      threshold,
      slaMet,
      gap: Math.round(gap * 100) / 100,
      status: slaMet ? 'compliant' : 'breach'
    };
  });

  // Identify breaches
  const breaches = periods
    .filter(p => !p.slaMet)
    .map(p => ({
      date: p.date,
      period: p.period,
      successRate: p.successRate,
      threshold,
      gap: p.gap,
      failedCount: p.failedCount,
      totalCount: p.total
    }));

  // Calculate overall SLA compliance
  const overallSlaMet = trends.overall.successRate >= threshold;
  const overallGap = trends.overall.successRate - threshold;

  return {
    periods,
    overall: {
      slaMet: overallSlaMet,
      successRate: trends.overall.successRate,
      threshold,
      gap: Math.round(overallGap * 100) / 100,
      totalTasks: trends.overall.totalTasks,
      successCount: trends.overall.successCount,
      failedCount: trends.overall.failedCount,
      breachCount: breaches.length,
      complianceRate: periods.length > 0
        ? Math.round((periods.filter(p => p.slaMet).length / periods.length) * 10000) / 100
        : 0
    },
    breaches,
    summary: {
      totalPeriods: periods.length,
      compliantPeriods: periods.filter(p => p.slaMet).length,
      breachedPeriods: breaches.length,
      consecutiveBreaches: calculateConsecutiveBreaches(periods)
    }
  };
}

/**
 * Helper function to calculate consecutive breaches
 * @private
 */
function calculateConsecutiveBreaches(periods) {
  let maxConsecutive = 0;
  let currentConsecutive = 0;

  periods.forEach(period => {
    if (!period.slaMet) {
      currentConsecutive++;
      maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
    } else {
      currentConsecutive = 0;
    }
  });

  return maxConsecutive;
}

/**
 * Identify anomalies in backup tasks including unusual durations,
 * frequency gaps, and sudden failures.
 *
 * @param {Task[]} tasks - Array of backup tasks
 * @param {Object} options - Detection options
 * @param {number} options.durationStdDevThreshold - Standard deviations for duration anomalies (default: 2)
 * @param {number} options.frequencyGapHours - Hours without backup to flag as gap (default: 48)
 * @param {number} options.suddenFailureThreshold - Consecutive failures to flag (default: 3)
 * @returns {Object} Detected anomalies
 * @returns {Array} returns.durationAnomalies - Tasks with unusual durations
 * @returns {Array} returns.frequencyGaps - Periods without backups
 * @returns {Array} returns.suddenFailures - Sequences of failures
 * @returns {Object} returns.summary - Anomaly statistics
 *
 * @example
 * const anomalies = identifyAnomalies(tasks);
 * console.log(`Found ${anomalies.durationAnomalies.length} duration anomalies`);
 * console.log(`Found ${anomalies.frequencyGaps.length} frequency gaps`);
 */
export function identifyAnomalies(tasks, options = {}) {
  const {
    durationStdDevThreshold = 2,
    frequencyGapHours = 48,
    suddenFailureThreshold = 3
  } = options;

  if (!tasks || tasks.length === 0) {
    return {
      durationAnomalies: [],
      frequencyGaps: [],
      suddenFailures: [],
      summary: {
        totalAnomalies: 0,
        durationCount: 0,
        gapCount: 0,
        failureCount: 0
      }
    };
  }

  // Sort tasks by date
  const sortedTasks = [...tasks]
    .filter(t => t.finished_at)
    .sort((a, b) => new Date(a.finished_at).getTime() - new Date(b.finished_at).getTime());

  // 1. Detect duration anomalies
  const durationAnomalies = detectDurationAnomalies(sortedTasks, durationStdDevThreshold);

  // 2. Detect frequency gaps
  const frequencyGaps = detectFrequencyGaps(sortedTasks, frequencyGapHours);

  // 3. Detect sudden failures
  const suddenFailures = detectSuddenFailures(sortedTasks, suddenFailureThreshold);

  return {
    durationAnomalies,
    frequencyGaps,
    suddenFailures,
    summary: {
      totalAnomalies: durationAnomalies.length + frequencyGaps.length + suddenFailures.length,
      durationCount: durationAnomalies.length,
      gapCount: frequencyGaps.length,
      failureCount: suddenFailures.length
    }
  };
}

/**
 * Helper function to detect duration anomalies using standard deviation
 * @private
 */
function detectDurationAnomalies(tasks, stdDevThreshold) {
  // Calculate task durations
  const tasksWithDuration = tasks
    .filter(t => t.finished_at && t.created_at)
    .map(t => ({
      ...t,
      duration: new Date(t.finished_at).getTime() - new Date(t.created_at).getTime()
    }));

  if (tasksWithDuration.length < 3) {
    return []; // Need enough data for statistical analysis
  }

  // Calculate mean and standard deviation
  const durations = tasksWithDuration.map(t => t.duration);
  const mean = durations.reduce((sum, d) => sum + d, 0) / durations.length;
  const variance = durations.reduce((sum, d) => sum + Math.pow(d - mean, 2), 0) / durations.length;
  const stdDev = Math.sqrt(variance);

  // Find anomalies (beyond threshold standard deviations)
  const anomalies = tasksWithDuration
    .filter(t => Math.abs(t.duration - mean) > stdDev * stdDevThreshold)
    .map(t => ({
      taskId: t.id,
      resourceId: t.resource_id,
      duration: t.duration,
      durationMinutes: Math.round(t.duration / (60 * 1000) * 10) / 10,
      expectedDuration: Math.round(mean),
      expectedMinutes: Math.round(mean / (60 * 1000) * 10) / 10,
      deviation: Math.round(((t.duration - mean) / stdDev) * 100) / 100,
      timestamp: t.finished_at,
      type: t.duration > mean ? 'slower-than-expected' : 'faster-than-expected',
      severity: Math.abs(t.duration - mean) > stdDev * stdDevThreshold * 1.5 ? 'high' : 'medium'
    }));

  return anomalies;
}

/**
 * Helper function to detect frequency gaps (periods without backups)
 * @private
 */
function detectFrequencyGaps(tasks, gapHours) {
  const gaps = [];
  const gapMs = gapHours * 60 * 60 * 1000;

  for (let i = 1; i < tasks.length; i++) {
    const prevTime = new Date(tasks[i - 1].finished_at).getTime();
    const currentTime = new Date(tasks[i].finished_at).getTime();
    const gap = currentTime - prevTime;

    if (gap > gapMs) {
      gaps.push({
        startDate: tasks[i - 1].finished_at,
        endDate: tasks[i].finished_at,
        gapDuration: gap,
        gapHours: Math.round(gap / (60 * 60 * 1000) * 10) / 10,
        gapDays: Math.round(gap / (24 * 60 * 60 * 1000) * 10) / 10,
        previousTask: tasks[i - 1].id,
        nextTask: tasks[i].id,
        severity: gap > gapMs * 2 ? 'high' : 'medium'
      });
    }
  }

  return gaps;
}

/**
 * Helper function to detect sudden failure sequences
 * @private
 */
function detectSuddenFailures(tasks, threshold) {
  const failures = [];
  let consecutiveFailures = [];

  tasks.forEach(task => {
    if (task.status === 'failed') {
      consecutiveFailures.push(task);
    } else if (consecutiveFailures.length >= threshold) {
      // End of failure sequence
      failures.push({
        startDate: consecutiveFailures[0].finished_at,
        endDate: consecutiveFailures[consecutiveFailures.length - 1].finished_at,
        failureCount: consecutiveFailures.length,
        tasks: consecutiveFailures.map(t => ({
          id: t.id,
          resourceId: t.resource_id,
          timestamp: t.finished_at
        })),
        affectedResources: [...new Set(consecutiveFailures.map(t => t.resource_id))],
        severity: consecutiveFailures.length >= threshold * 2 ? 'critical' : 'high'
      });
      consecutiveFailures = [];
    } else {
      consecutiveFailures = [];
    }
  });

  // Check if we ended with a failure sequence
  if (consecutiveFailures.length >= threshold) {
    failures.push({
      startDate: consecutiveFailures[0].finished_at,
      endDate: consecutiveFailures[consecutiveFailures.length - 1].finished_at,
      failureCount: consecutiveFailures.length,
      tasks: consecutiveFailures.map(t => ({
        id: t.id,
        resourceId: t.resource_id,
        timestamp: t.finished_at
      })),
      affectedResources: [...new Set(consecutiveFailures.map(t => t.resource_id))],
      severity: consecutiveFailures.length >= threshold * 2 ? 'critical' : 'high',
      ongoing: true
    });
  }

  return failures;
}

// Export all functions as named exports
export default {
  calculateHistoricalTrends,
  calculateProtectionTimeline,
  forecastGrowth,
  generateSLAReport,
  identifyAnomalies
};
